
import React from "react";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export interface ColumnOption {
  id: string;
  label: React.ReactNode;
  required?: boolean;
}

export interface ColumnSelectorProps {
  availableColumns: ColumnOption[];
  selectedColumns: string[];
  onColumnChange: (columns: string[]) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ColumnSelector({ 
  availableColumns, 
  selectedColumns, 
  onColumnChange,
  open,
  onOpenChange
}: ColumnSelectorProps) {
  const [localSelectedColumns, setLocalSelectedColumns] = React.useState<string[]>(selectedColumns);

  React.useEffect(() => {
    setLocalSelectedColumns(selectedColumns);
  }, [selectedColumns]);

  const handleColumnToggle = (columnId: string, checked: boolean) => {
    const option = availableColumns.find(col => col.id === columnId);
    if (option?.required) return;

    setLocalSelectedColumns(prev => {
      if (checked) {
        return [...prev, columnId];
      } else {
        return prev.filter(id => id !== columnId);
      }
    });
  };

  const handleApply = () => {
    onColumnChange(localSelectedColumns);
    if (onOpenChange) {
      onOpenChange(false);
    }
  };

  const handleResetToDefault = () => {
    const defaults = availableColumns
      .filter(col => !col.id.includes("custom") && col.id !== "group")
      .map(col => col.id);
    setLocalSelectedColumns(defaults);
  };

  const content = (
    <div className="p-4 space-y-4">
      <p className="text-sm text-muted-foreground">
        Select the columns you want to display in the table.
      </p>
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {availableColumns.map((column) => (
          <div key={column.id} className="flex items-center space-x-2">
            <Checkbox
              id={`column-${column.id}`}
              checked={localSelectedColumns.includes(column.id)}
              onCheckedChange={(checked) => handleColumnToggle(column.id, checked === true)}
              disabled={column.required}
            />
            <label
              htmlFor={`column-${column.id}`}
              className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${
                column.required ? "opacity-70" : ""
              }`}
            >
              {column.label}
              {column.required && <span className="ml-1 text-xs text-muted-foreground">(required)</span>}
            </label>
          </div>
        ))}
      </div>
      <Separator />
      <div className="flex justify-between">
        <Button variant="outline" size="sm" onClick={handleResetToDefault}>
          <ArrowLeftRight className="mr-2 h-4 w-4" />
          Reset to Default
        </Button>
        <Button onClick={handleApply}>Apply</Button>
      </div>
    </div>
  );

  if (open === undefined || onOpenChange === undefined) {
    return content;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Table Columns</DialogTitle>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
